import { useStore } from 'effector-react';
import { useEffect } from 'react';
import FilterLine from 'components/FilterLine';
import FilterResult from 'components/FilterResult';
import LoadMoreButton from 'components/LoadMoreButton';
import NoContent from 'components/NoContent';
import {
	clearPiiFilter,
	piiFilterToArray,
	piiFilterWithGroupsStore,
} from 'components/PiiGlobalFilterV2/model';
import Preloader from 'components/Preloader';
import ResetFilters from 'components/ResetFilters';
import EnhancedTableHead, { TOrder } from 'components/table/EnhancedTableHead';
import GridBody from 'components/table/GridBody';
import GridTable from 'components/table/GridTable';
import { Typo } from 'components/typography/Typo';
import { NoSQLDatabaseItem, NoSQLDatabaseCollectionItem } from 'models/noSQLDatabases/dto';
import { noSQLDatabaseCollectionsModel } from 'models/noSQLDatabases/model';
import { PageParamsConfig, usePageParams } from 'services/pageParams';
import commonStyles from '../../index.module.css';
import tableConfig from './config';
import styles from './index.module.css';
import { TableRow } from './TableRow';

const pageConfig = {
	'data-types': {
		type: 'numberArray',
		persistence: 'session',
	},
	nonempty: {
		type: 'boolean',
		persistence: 'session',
	},
	sort: {
		type: 'sort',
		persistence: 'session',
	},
} satisfies PageParamsConfig;

type Props = {
	databaseId: NoSQLDatabaseItem['id'];
};

export function CollectionsTable({ databaseId }: Readonly<Props>) {
	const state = useStore(noSQLDatabaseCollectionsModel.store);
	const piiFilterWithGroups = useStore(piiFilterWithGroupsStore);
	const [pageParams, setPageParams] = usePageParams(pageConfig, 'nosql-database-collections');

	useEffect(() => {
		setPageParams({
			...pageParams,
			'data-types': piiFilterWithGroups.dataTypes,
			nonempty: piiFilterWithGroups.nonEmpty,
		});
	}, [piiFilterWithGroups]);

	useEffect(() => {
		const { sort } = pageParams;

		const dataTypes = piiFilterToArray(piiFilterWithGroups);
		const paramsForFx = {
			id: databaseId,
			sort: { orderBy: sort.value as keyof NoSQLDatabaseCollectionItem, order: sort.operator },
			'data-types': dataTypes,
		};

		noSQLDatabaseCollectionsModel.fetchFx(paramsForFx);
	}, [pageParams]);

	// Clean up model on unmount
	useEffect(() => {
		return () => {
			noSQLDatabaseCollectionsModel.resetFx();
		};
	}, []);

	function onSortUpdate(property: keyof NoSQLDatabaseCollectionItem) {
		const { sort } = pageParams;

		const operator: TOrder = sort.value === property && sort.operator === 'asc' ? 'desc' : 'asc';
		const newParams = { sort: { operator, value: property } };

		setPageParams({ ...pageParams, ...newParams });
	}

	function onResetFilters() {
		const newParams = {
			'data-types': [],
		};

		setPageParams({ ...pageParams, ...newParams });
		clearPiiFilter();
	}
	const { status, data, total, total_filtered, hasMoreData } = state;

	const hasFilter = pageParams['data-types'].length > 0;

	function contentWithCondition() {
		if (data.length > 0) {
			return data.map((collection) => <TableRow collection={collection} key={collection.id} />);
		} else if (hasFilter) {
			return <ResetFilters onReset={onResetFilters} />;
		} else {
			return (
				<NoContent type="databaseTables" className={styles.rowContainer}>
					<div className={commonStyles.emptyContainer}>
						<Typo variant="D/Regular/Body-S" color="secondary">
							No available analytics.
						</Typo>
					</div>
				</NoContent>
			);
		}
	}

	return (
		<>
			<FilterLine
				config={['dataTypes']}
				values={{
					dataTypes: pageParams['data-types'],
				}}
				onChange={() => {}}
			/>

			<FilterResult
				entityLabel="collection"
				loading={status === 'loading'}
				total={total}
				totalFiltered={total_filtered}
			/>

			<GridTable dataTest="nosql-databases-collection-table">
				<EnhancedTableHead
					config={tableConfig}
					order={pageParams.sort.operator}
					orderBy={pageParams.sort.value}
					onRequestSort={onSortUpdate}
					rowClassname={styles.rowContainer}
				/>

				<Preloader isLoading={status === 'initial' || status === 'loading'}>
					<GridBody data-test="nosql-databases-collection-list">
						{contentWithCondition()}

						<LoadMoreButton
							show={hasMoreData}
							loading={status === 'loadingMore'}
							request={noSQLDatabaseCollectionsModel.fetchMoreFx}
						/>
					</GridBody>
				</Preloader>
			</GridTable>
		</>
	);
}
